import { Tag, cn } from '@glade-ai/ui';
import type { Icon } from '@phosphor-icons/react';
import Link from 'next/link';
import { type FC, useEffect, useRef } from 'react';

import s from './TabText.module.scss';

type Props = {
  label: string;
  selected: boolean;
  // TODO: migrate to sprite-based Icon from @glade-ai/ui (requires refactoring all callers to pass icon names)
  icon?: Icon;
  iconColor?: string;
  /** When provided and > 0, renders an error count badge inside the tab. */
  count?: number;
} & (
  | {
      href: string;
      onClick?: never;
    }
  | {
      href?: never;
      onClick: () => unknown;
    }
);

const TabText: FC<Props> = ({ label, icon: IconComponent, iconColor, selected, href, onClick, count }) => {
  const tabRef = useRef<HTMLButtonElement>(null);
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const currentRef = href ? anchorRef.current : tabRef.current;
    if (selected && currentRef) {
      currentRef.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [selected, href]);

  const sharedProps = {
    className: cn(s.container, 'gap-2 items-center'),
    style: selected ? { background: 'var(--color-gray-25)' } : {},
  };

  return href ? (
    <Link ref={anchorRef} prefetch={false} href={href} {...sharedProps}>
      {IconComponent && <IconComponent size={12} weight='fill' color={iconColor || 'var(--color-primary)'} />}
      {label}
      {(count ?? 0) > 0 && <Tag className='ms-1 shrink-0 border-transparent bg-warning text-white'>{count}</Tag>}
    </Link>
  ) : (
    <button ref={tabRef} onClick={onClick} {...sharedProps}>
      {IconComponent && <IconComponent size={12} weight='fill' color={iconColor || 'var(--color-primary)'} />}
      {label}
      {(count ?? 0) > 0 && <Tag className='ms-1 shrink-0 border-transparent bg-warning text-white'>{count}</Tag>}
    </button>
  );
};

export default TabText;
